import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PublicIcon from "@mui/icons-material/Public";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LockIcon from "@mui/icons-material/Lock";
import WorkIcon from "@mui/icons-material/Work";
import { isEmail } from "validator";

import { navigateToAdminUsersCreateEngagement } from "../../routing/route-actions";
import { StoreState } from "../../redux/configure-store";
import { navigateToAdminPage } from "../../routing/route-actions";
import { isValidPassword, passwordRequirements } from "../../common/utils";

import { updateUserForAdmin } from "../admin-actions";
import { backgrounds } from "../../theme/theme";

const createInitialErrorState = () => ({
  firstName: "",
  lastName: "",
  country: "",
  numberOfHoursPerWeekAvailable: "",
  hourlyRateInUsd: "",
  shortTechnicalLabel: "",
  technicalDescription: "",
  paymentMethod: "",
  splitPaymentMethodPercentage: "",
  emailAddress: "",
  newPassword: "",
  confirmNewPassword: "",
});

interface AdminUsersViewProps {
  selectedUser: any;
  isEditingUser: boolean;
  updateUserForAdmin: (data: any) => Promise<boolean>;
  navigateToAdminUsersCreateEngagement: (userId: number) => void;
  navigateToAdminPage: () => void;
}

function AdminUsersView(props: AdminUsersViewProps) {
  const {
    selectedUser,
    isEditingUser,
    updateUserForAdmin,
    navigateToAdminUsersCreateEngagement,
    navigateToAdminPage,
  } = props;

  const [isInitialized, setIsInitialized] = useState(false);
  const [data, setData] = useState({
    id: 0,
    isAdmin: false,
    firstName: "",
    lastName: "",
    country: "",
    hourlyRateInUsd: "",
    shortTechnicalLabel: "",
    paymentMethod: "",
    splitPaymentMethodPercentage: 0,
    numberOfHoursPerWeekAvailable: 0,
    technicalDescription: "",
    emailAddress: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState(createInitialErrorState());

  useEffect(() => {
    if (selectedUser && !isInitialized) {
      setIsInitialized(true);
      setData({
        id: selectedUser.id,
        isAdmin: selectedUser.isAdmin,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        emailAddress: selectedUser.emailAddress || "",
        technicalDescription: selectedUser.technicalDescription || "",
        country: selectedUser.country || "",
        hourlyRateInUsd: selectedUser.hourlyRateInUsd || "",
        shortTechnicalLabel: selectedUser.shortTechnicalLabel || "",
        paymentMethod: selectedUser.paymentMethod || "",
        splitPaymentMethodPercentage: selectedUser.splitPaymentMethodPercentage || 0,
        numberOfHoursPerWeekAvailable:
          selectedUser.numberOfHoursPerWeekAvailable || 0,
        newPassword: "",
        confirmNewPassword: "",
      });
      setErrors(createInitialErrorState());
    }
  }, [selectedUser, isInitialized]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSelectChange = (e: SelectChangeEvent<string>) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = createInitialErrorState();

    if (data.firstName.length === 0) {
      newErrors.firstName = "Can't be void";
    }

    if (data.lastName.length === 0) {
      newErrors.lastName = "Can't be void";
    }

    if (!data.emailAddress || !isEmail(data.emailAddress))
      newErrors.emailAddress = "Invalid email";

    if (data.newPassword && !isValidPassword(data.newPassword))
      newErrors.newPassword = "Password is too weak";

    if (data.newPassword !== data.confirmNewPassword) {
      newErrors.confirmNewPassword = "Passwords do not match";
    }

    const splitPaymentMethodPercentageNumber = Number(data.splitPaymentMethodPercentage);
    if (!Number.isInteger(splitPaymentMethodPercentageNumber) || Number(splitPaymentMethodPercentageNumber) > 100 || Number(splitPaymentMethodPercentageNumber) < 0) {
      newErrors.splitPaymentMethodPercentage = "Split Payment Percentage must be an integer betweeo 0 and 100";
    }

    const hasErrors = Object.values(newErrors).some(
      (error: string) => error.length !== 0
    );

    if (!hasErrors) {
      await updateUserForAdmin(data);
      navigateToAdminPage();
    } else {
      setErrors(newErrors);
    }
  };

  if (!selectedUser) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: backgrounds.page,
        py: 4,
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          mx: "auto",
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "primary.main",
              mb: 2,
            }}
          >
            <PersonIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Typography component="h1" variant="h5" fontWeight={600}>
            Edit User
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Update user profile and settings
          </Typography>
        </Box>

        {/* User Info Card */}
        <Box
          sx={{
            backgroundColor: backgrounds.surface,
            borderRadius: 1,
            p: 2,
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              User ID
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {data.id}
            </Typography>
          </Box>
          <Button
            onClick={() => navigateToAdminUsersCreateEngagement(data.id)}
            variant="contained"
            color="secondary"
            startIcon={<WorkIcon />}
            size="small"
          >
            Create Engagement
          </Button>
        </Box>

        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            {/* Basic Info Section */}
            <Grid item xs={12} sm={6}>
              <TextField
                id="firstName"
                name="firstName"
                label="First Name"
                autoFocus
                autoComplete="off"
                value={data.firstName}
                onChange={onChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="lastName"
                name="lastName"
                label="Last Name"
                autoComplete="off"
                value={data.lastName}
                onChange={onChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="emailAddress"
                name="emailAddress"
                label="Email Address"
                type="email"
                autoComplete="off"
                value={data.emailAddress}
                onChange={onChange}
                error={!!errors.emailAddress}
                helperText={errors.emailAddress}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="country"
                name="country"
                label="Country"
                autoComplete="off"
                value={data.country}
                onChange={onChange}
                error={!!errors.country}
                helperText={errors.country}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PublicIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Technical Profile
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="shortTechnicalLabel"
                name="shortTechnicalLabel"
                label="Short Technical Label"
                placeholder="frontend, backend, fullstack..."
                autoComplete="off"
                value={data.shortTechnicalLabel}
                onChange={onChange}
                error={!!errors.shortTechnicalLabel}
                helperText={errors.shortTechnicalLabel}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="technicalDescription"
                name="technicalDescription"
                label="Technical Description"
                autoComplete="off"
                multiline
                rows={4}
                value={data.technicalDescription}
                onChange={onChange}
                error={!!errors.technicalDescription}
                helperText={errors.technicalDescription}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Availability & Rates
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="numberOfHoursPerWeekAvailable"
                name="numberOfHoursPerWeekAvailable"
                label="Hours Per Week Available"
                autoComplete="off"
                value={data.numberOfHoursPerWeekAvailable}
                onChange={onChange}
                error={!!errors.numberOfHoursPerWeekAvailable}
                helperText={errors.numberOfHoursPerWeekAvailable}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="hourlyRateInUsd"
                name="hourlyRateInUsd"
                label="Hourly Rate (USD)"
                autoComplete="off"
                value={data.hourlyRateInUsd}
                onChange={onChange}
                error={!!errors.hourlyRateInUsd}
                helperText={errors.hourlyRateInUsd}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Payment Settings
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.paymentMethod}>
                <InputLabel id="payment-method-label">Payment Method</InputLabel>
                <Select
                  labelId="payment-method-label"
                  id="payment-method"
                  value={data.paymentMethod}
                  label="Payment Method"
                  name="paymentMethod"
                  onChange={onSelectChange}
                >
                  <MenuItem value="wise">Wise</MenuItem>
                  <MenuItem value="bmoUsd">BMO USD</MenuItem>
                  <MenuItem value="bitcoin">Bitcoin</MenuItem>
                  <MenuItem value="stablecoin">Stablecoin</MenuItem>
                </Select>
                {errors.paymentMethod && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.paymentMethod}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="splitPaymentMethodPercentage"
                name="splitPaymentMethodPercentage"
                label="BTC Payment Percentage"
                autoComplete="off"
                value={data.splitPaymentMethodPercentage}
                onChange={onChange}
                error={!!errors.splitPaymentMethodPercentage}
                helperText={errors.splitPaymentMethodPercentage}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Change Password (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="newPassword"
                name="newPassword"
                label="New Password"
                type="password"
                autoComplete="off"
                value={data.newPassword}
                onChange={onChange}
                error={!!errors.newPassword}
                helperText={errors.newPassword || passwordRequirements}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="confirmNewPassword"
                name="confirmNewPassword"
                label="Confirm New Password"
                type="password"
                autoComplete="off"
                value={data.confirmNewPassword}
                onChange={onChange}
                error={!!errors.confirmNewPassword}
                helperText={errors.confirmNewPassword}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={isEditingUser}
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  isEditingUser: storeState.admin.isEditingUser,
  user: storeState.authentication.user,
});

export default connect(mapStateToProps, {
  updateUserForAdmin,
  navigateToAdminUsersCreateEngagement,
  navigateToAdminPage,
})(AdminUsersView);
